import React, { useState } from "react";
import { Car, ChefHat, BellRing, PartyPopper, Plane, Castle } from "lucide-react";
import { ESTATES } from "./data";
import { useRevealGroup } from "../../hooks/useReveal";
import { useViewRouter } from "../../hooks/useViewRouter";

const services = [
  { icon: Car, label: "Chauffeur" },
  { icon: ChefHat, label: "Private Chef" },
  { icon: BellRing, label: "Butler" },
  { icon: PartyPopper, label: "Events" },
  { icon: Plane, label: "Concierge" },
  { icon: Castle, label: "Heritage" },
];

const Estates = () => {
  const ref = useRevealGroup(".reveal");
  const { openContactModal } = useViewRouter();
  const [region, setRegion] = useState("domestic"); // 'domestic' | 'international'

  return (
    <section ref={ref} className="engrave-dark bg-forest-tex py-24 lg:py-32" style={{ "--engrave-img": "url('/brand/hnmb-logo.tight.svg')" }} data-testid="estates-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="reveal max-w-[820px]">
          <div className="flex items-center gap-3 mb-6">
            <span className="block w-10 h-px bg-num-gold" />
            <span className="font-ui text-[9px] uppercase text-num-gold track-eyebrow">
              Numbardar Estates
            </span>
          </div>
          <h2
            className="font-display font-medium text-num-cream leading-[1.05]"
            style={{ fontSize: "clamp(38px, 5vw, 60px)" }}
          >
            A Life{" "}
            <span className="font-italic italic font-normal text-num-gold-light">
              Uncommonly Lived
            </span>
          </h2>
          <p
            className="font-body italic text-[15px] mt-6"
            style={{ color: "rgba(247,243,236,0.55)" }}
          >
            Handpicked properties. Bespoke services. Landscapes that stay with you.
          </p>
        </div>

        {/* Domestic / International toggle */}
        <div className="reveal mt-10 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setRegion("domestic")}
            className={`font-ui px-6 py-2.5 transition-colors uppercase ${
              region === "domestic"
                ? "bg-num-gold text-num-deep border border-num-gold"
                : "bg-transparent text-num-gold border border-num-gold/40 hover:border-num-gold"
            }`}
            style={{ fontSize: 10, letterSpacing: "0.3em" }}
            data-testid="estates-toggle-domestic"
          >
            Domestic
          </button>
          <button
            type="button"
            onClick={() => setRegion("international")}
            className={`font-ui px-6 py-2.5 transition-colors uppercase ${
              region === "international"
                ? "bg-num-gold text-num-deep border border-num-gold"
                : "bg-transparent text-num-gold border border-num-gold/40 hover:border-num-gold"
            }`}
            style={{ fontSize: 10, letterSpacing: "0.3em" }}
            data-testid="estates-toggle-international"
          >
            International — Coming Soon
          </button>
        </div>

        {/* Cards (with optional overlay) */}
        <div className="relative mt-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ESTATES.map((e, i) => (
              <article
                key={e.name}
                className={`reveal delay-${i + 1} group relative overflow-hidden bg-num-deep aspect-[3/4]`}
                data-testid={`estate-card-${i}`}
              >
                <div className="img-zoom absolute inset-0">
                  <img
                    src={e.image}
                    alt={e.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to bottom, rgba(28,6,6,0.05) 35%, rgba(28,6,6,0.85) 100%)",
                  }}
                />
                <div className="relative h-full flex flex-col justify-between p-6">
                  <span className="self-start font-ui text-[8px] uppercase track-eyebrow bg-num-gold text-num-deep px-2.5 py-1">
                    Estates
                  </span>
                  <div>
                    <div className="font-display font-medium text-num-cream text-[22px] leading-tight">
                      {e.name}
                    </div>
                    <div
                      className="font-ui text-[10px] track-label uppercase mt-2"
                      style={{ color: "rgba(247,243,236,0.7)" }}
                    >
                      {e.location}
                    </div>
                    <div className="font-ui text-[11px] mt-3 text-num-gold-light">
                      {e.availability}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* "Coming Soon" overlay */}
          {region === "international" && (
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                background: "rgba(28,6,6,0.92)",
                backdropFilter: "blur(4px)",
                WebkitBackdropFilter: "blur(4px)",
              }}
              data-testid="international-overlay"
            >
              <div className="text-center max-w-[420px] px-6">
                <div
                  className="font-ui text-[9px] uppercase text-num-gold track-eyebrow mb-4"
                  style={{ letterSpacing: "0.5em" }}
                >
                  International
                </div>
                <h3
                  className="font-display font-medium text-num-cream leading-[1.05]"
                  style={{ fontSize: "clamp(28px, 3.4vw, 38px)" }}
                >
                  Coming{" "}
                  <span className="font-italic italic font-normal text-num-gold-light">Soon</span>
                </h3>
                <p
                  className="font-body italic text-[14px] mt-4"
                  style={{ color: "rgba(247,243,236,0.6)" }}
                >
                  Numbardar Estates expands to Dubai, London and beyond. Join the early
                  list to receive invitation-only previews.
                </p>
                <button
                  type="button"
                  onClick={openContactModal}
                  className="mt-7 btn-gold inline-block px-7 py-3 font-ui text-[10px] uppercase track-wide-3"
                  style={{ letterSpacing: "0.36em" }}
                  data-testid="international-waitlist"
                >
                  Join the Waitlist
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Service strip */}
        <div className="reveal mt-16 grid grid-cols-3 sm:grid-cols-6 gap-6 border-t border-[rgba(200,169,90,0.2)] pt-10">
          {services.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex flex-col items-center gap-3 text-num-cream/85"
              data-testid={`estate-service-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon size={22} strokeWidth={1.2} className="text-num-gold" />
              <span className="font-ui text-[10px] uppercase track-label">{label}</span>
            </div>
          ))}
        </div>

        <div className="mt-14 text-center">
          <button
            type="button"
            onClick={openContactModal}
            className="btn-gold inline-block px-8 py-4 font-ui text-[10px] uppercase track-wide-3"
            style={{ letterSpacing: "0.36em" }}
            data-testid="estates-cta"
          >
            Enquire About a Stay
          </button>
        </div>
      </div>
    </section>
  );
};

export default Estates;
